<template>
  <article :id="product.slug" class="supply-card-grid" ref="productCard" v-b-hover="handleHover">
    <b-row class="description py-1">
      <!-- image -->
      <b-col cols="6">
        <b-overlay class="h-100" :show="isHovered" variant="dark" blur=".3" opacity=".25" rounded="sm">
          <template #overlay>
            <div class="d-flex justify-content-center">
              <b-button
                class="btn-wishlist"
                :class="{ added: inWishlist }"
                :variant="inWishlist ? 'primary' : 'dark'"
                :title="$trans('storefront::product_card.wishlist')"
                @click="syncWishlist"
              >
                <i class="la-heart" :class="inWishlist ? 'las' : 'lar'"></i>
              </b-button>
            </div>
          </template>
          <a :href="productUrl" class="image">
            <img :src="baseImage" :class="{ 'image-placeholder': !hasBaseImage }" alt="product image" />
          </a>
        </b-overlay>
      </b-col>

      <!-- details -->
      <b-col class="d-flex flex-column">
        <div id="limitPurchaseLabel"
             v-if="product.is_purchase_limit"
             v-b-popover.hover.top="purchaseLimitTooltipText"
             class="limit-purchase-badge mr-0 mb-2 width-fit-content">
          <small>${{ this.dailyPurchaseLimitAmount }} per day</small>
        </div>
        <h6 class="d-flex" data-toggle="tooltip" data-placement="top" :title="product.name">
          <a :href="productUrl" class="prod-name" v-text="product.name"></a>
          <a v-if="isAdmin" :href="`/admin/products/${product.id}`" target="_blank">
            <edit2-icon></edit2-icon>
          </a>
        </h6>
        <strong class="price">${{ currencyFormat(product.price.formatted) }}</strong>
        <small class="text-muted">Each</small>
      </b-col>
    </b-row>

    <div class="bottom">
      <b-overlay :show="apiLoading" spinner-variant="primary" blur=".3" opacity=".75" rounded="sm">
        <div class="add-to-cart flex-column flex-lg-row">
          <div class="number-picker w-100 w-lg-50">
            <input
              v-integer-only
              type="text"
              :value="productQty"
              min="1"
              :max="maxSellingQty"
              id="qty"
              class="form-control input-number input-quantity"
              @input="updateQuantity($event.target.value)"
              @keydown.up="updateQuantity(productQty + 1)"
              @keydown.down="updateQuantity(productQty - 1)"
            />
            <span class="btn-wrapper">
              <button
                type="button"
                @click="updateQuantity(productQty + 1)"
                class="btn btn-number btn-plus"
                :disabled="productQty >= maxSellingQty"
                data-type="plus"
              >
                +
              </button>
              <button
                type="button"
                :disabled="productQty >= maxSellingQty"
                @click="updateQuantity(productQty - 1)"
                class="btn btn-number btn-minus text-danger"
                data-type="minus"
              >
                -
              </button>
            </span>
          </div>
          <button
            v-if="hasNoOption || product.is_out_of_stock"
            class="btn btn-primary btn-add-to-cart w-100 w-lg-50"
            :class="{ 'btn-loading': addingToCart }"
            :disabled="product.is_out_of_stock || giftProduct"
            @click="addToCart"
          >
            {{ $trans("storefront::product_card.add_to_cart") }}
          </button>
        </div>
      </b-overlay>

      <qd v-if="product.qd_enabled && isHovered && product.quantity_discount_with_min_max_front.length > 1"
          :items="product.quantity_discount_with_min_max_front" />
    </div>
  </article>
</template>

<script>
import ProductRating from "./ProductRating.vue"
import ProductCardMixin from "../mixins/ProductCardMixin"
import { currencyFormat } from "../functions"
import QD from "./QD"
import { Edit2Icon } from "vue-feather-icons"
import store from "../store"

export default {
  mixins: [ProductCardMixin],
  components: { qd: QD, ProductRating, Edit2Icon },
  props: ["product", "isAdmin", "dailyPurchaseLimitAmount", "hotProductsDisclaimer", "payment_methods"],
  data() {
    return {
      currencyFormat,
      isHovered: false
    }
  },
  methods: {
    handleHover(hovered) {
      this.isHovered = hovered
    }
  },
  computed: {
    purchaseLimitTooltipText() {
      return "There is a $" + this.dailyPurchaseLimitAmount + " USD per person, per day limit for any combination of items that are specifically marked with the red $" + this.dailyPurchaseLimitAmount + " USD per day tag. This limit does not apply to any banknotes that do not have this tag."
    },
    giftProduct() {
      return store.state.cart.gift_products.find(item => item.id == this.product.id) !=  undefined
    }
  }

}
</script>

<style lang="scss">
.supply-card-grid {
  display: flex !important;
  height: 100%;
  width: 100%;
  flex-direction: column;
  padding: 16px;
  border: 1.5px solid #cfcfcf;
  border-radius: 8px;
  position: relative;

  .price {
    font-size: 18px;
    color: var(--primary);
    font-weight: 700;
  }

  .image {
    cursor: pointer;

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .btn-wishlist {
    font-size: 16px;
    height: 35px;
    width: 35px;
    padding: 0;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--secondary);
    border-radius: 15%;

    > i {
      color: #fff;
    }
  }

  .bottom {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .add-to-cart {
      display: flex;
      gap: 10px;

      .number-picker {
        display: inline-flex;
        align-items: center;
        position: relative;
        width: 50%;
        height: 40px;
      }

      .number-picker label {
        font-weight: 500;
        margin-bottom: 0;
      }

      .number-picker .input-quantity {
        padding: 10px 39px 10px 10px;
        height: 40px;
        text-align: center;
        font-size: 18px;
        font-weight: bold;
      }

      .number-picker .input-quantity:focus {
        outline: 0;
        border-color: #ebebeb;
      }

      .number-picker .btn-wrapper {
        position: absolute;
        top: 0;
        right: 0;
      }

      .number-picker .btn-wrapper .btn-number {
        cursor: pointer;
        position: absolute;
        right: 0;
        width: 30px;
        padding: 0;
        line-height: 18px;
        background: none;
        border: 1px solid #ebebeb;
        border-radius: 0;
      }

      .number-picker .btn-wrapper .btn-number:hover {
        color: #DEAA5E;
      }

      .number-picker .btn-wrapper .btn-number[disabled]:hover {
        color: #191919;
      }

      .number-picker .btn-wrapper .btn-plus {
        top: 0;
        color: #DEAA5E;
        height: 20px;
        border-top-right-radius: 2px;
        border-bottom: 1px solid #ebebeb;
        border-left: 1px solid #ebebeb;
      }

      .number-picker .btn-wrapper .btn-minus {
        top: 20px;
        height: 20px;
        border-bottom-right-radius: 2px;
        border-left: 1px solid #ebebeb;
      }

      .btn-add-to-cart {
        padding: 10px;
        width: 50%;
        line-height: 20px;
        border-radius: 4px;
        font-size: 14px;
      }
    }
  }
}

.text-muted {
  color: #b5b5b5 !important;
}

h6 {
  > .prod-name {
    display: -webkit-box;
    height: 3.5rem;
    -webkit-line-clamp: 3; /* Limit the content to 3 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    font-size: 16px;
    font-weight: 500;
    line-height: normal
  }
}

.limit-purchase-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 30px;
  padding: 4px 14px;
  gap: 5px;

  img {
    width: 16px;
  }

  small {
    color: #ffffff;
    font-size: 12px;
  }
}

.limit-purchase-badge {
  background: red;
}
</style>
